'use client';

import { CodeSandboxLogoIcon } from '@radix-ui/react-icons';
import {
  Column,
  ColumnDef,
  InitialTableState,
  OnChangeFn,
  PaginationState,
  Row,
  RowSelectionState,
  SortingState,
  flexRender,
  getCoreRowModel,
  getPaginationRowModel,
  getSortedRowModel,
  useReactTable,
} from '@tanstack/react-table';
import { CHECKBOX_COLUMN_ID } from 'piramid-constants';
import {
  Button,
  DataTablePagination,
  ScrollArea,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  cn,
  useSyncedHorizontalScrollbar,
} from 'ui';

export const DataTableTitle = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <h1 className={cn('text-foreground font-medium text-2xl', className)}>
      {children}
    </h1>
  );
};

export const DataTableContainer = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <div className={cn('w-full max-w-7xl space-y-4 bg-stone-50', className)}>
      {children}
    </div>
  );
};

export const DataTableHeader = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return (
    <div
      className={cn('flex flex-row items-center justify-between', className)}
    >
      {children}
    </div>
  );
};

export const DataTableDescription = ({
  children,
  className,
}: {
  children: React.ReactNode;
  className?: string;
}) => {
  return <p className={cn('text-sm zinc-400', className)}>{children}</p>;
};

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  emptyStateMessage?: string;
}

// Columns opt into a shrink-to-content width by setting `meta.actionsColumn`
// (e.g. `columnDef.meta = { actionsColumn: true }`). Handy for the trailing
// actions-dropdown column so it stops eating the remaining table width. The
// `w-[1%] whitespace-nowrap` pair is the canonical CSS trick to collapse a
// cell in a full-width table down to its content.
const isActionsColumn = <TData,>(column: Column<TData, unknown>): boolean =>
  (column.columnDef.meta as { actionsColumn?: boolean } | undefined)
    ?.actionsColumn === true;

const ACTIONS_COLUMN_CLASS = 'w-16 whitespace-nowrap';

type ColumnAlign = 'left' | 'center' | 'right';

// Per-column horizontal alignment, opted into via `columnDef.meta.align`
// (e.g. `columnDef.meta = { align: 'right' }`). When set, it aligns BOTH the
// header and the cell content of that column. Backwards compatible: the legacy
// boolean `meta.align: true` is intentionally ignored here (returns undefined),
// so columns that used it keep their previous behavior untouched.
const getColumnAlign = <TData,>(
  column: Column<TData, unknown>,
): ColumnAlign | undefined => {
  const align = (column.columnDef.meta as { align?: unknown } | undefined)
    ?.align;

  return align === 'left' || align === 'center' || align === 'right'
    ? align
    : undefined;
};

const HEADER_ALIGN_CLASS: Record<ColumnAlign, string> = {
  left: 'text-left',
  center: 'text-center',
  right: 'text-right',
};

const CELL_ALIGN_CLASS: Record<ColumnAlign, string> = {
  left: 'justify-start',
  center: 'justify-center',
  right: 'justify-end',
};

// `compact` shrinks the vertical padding of body cells (default `p-4`) and the
// header row (default `h-12`) for a denser table.
const COMPACT_CELL_CLASS = '!py-1.5';
const COMPACT_HEADER_CLASS = '!h-auto !py-1.5';

// Per-column width, opted into via `columnDef.meta.widthClassName` — any Tailwind
// width utility (`w-8`, `w-[120px]`, `max-w-xs`, `min-w-[20rem]`, …). When set, it
// is applied to BOTH the header and the cells of that column. Backwards
// compatible: columns without it are unaffected.
//
// La tabla es `table-layout: auto`: un `width` sobre la celda es solo una
// sugerencia que el navegador descarta cuando las columnas compiten por el
// ancho. Para ENSANCHAR usar `min-w-*` (el mínimo sí se respeta y desborda al
// contenedor, que es `overflow-auto`); `w-*`/`max-w-*` sirven para angostar.
const getColumnWidthClassName = <TData,>(
  column: Column<TData, unknown>,
): string | undefined => {
  const widthClassName = (
    column.columnDef.meta as { widthClassName?: unknown } | undefined
  )?.widthClassName;

  return typeof widthClassName === 'string' ? widthClassName : undefined;
};

interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  CellWrapper?: React.FC<{
    row: TData;
    children: React.ReactNode;
    column: Column;
  }>;
  // Column ids whose cells should NOT be wrapped in `CellWrapper`. Use it for an
  // interactive cell (e.g. an actions dropdown/dialog) in a table whose rows are
  // otherwise a navigation Link, so clicking the action does not trigger the row
  // navigation nor get intercepted by the stretched-link overlay.
  ignoreCellWrapperColumnIds?: string[];
}

// Clicking one of these does not navigate: it already has its own action.
const INTERACTIVE_CELL_CONTENT_SELECTOR =
  'a, button, input, select, textarea, label, [role="button"], [role="checkbox"], [role="menuitem"], [data-row-click-ignore]';

export function DataTable<TData, TValue>({
  emptyStateMessage = 'No se encontraron resultados.',
  columns,
  data,
  CellWrapper,
  ignoreCellWrapperColumnIds,
  withPagination = false,
  onClickRow,
  tableRowClassName,
  className,
  entityLabel,
  align = 'left',
  compact = false,
  sorting,
  onSortingChange,
  rowSelection = {},
  onRowSelectionChange,
  enableRowSelection = false,
  getRowId,
  initialState,
  classNames,
  withTopScrollbar = false,
}: DataTableProps<TData, TValue> & {
  tableRowClassName?: ((row: Row<TData>) => string) | string;
  withPagination?: boolean;
  onClickRow?: (row: Row<TData>) => void;
  className?: string;
  entityLabel?: string;
  align?: 'center' | 'left';
  // Denser rows: reduces the vertical padding of every body cell.
  compact?: boolean;
  sorting?: SortingState;
  onSortingChange?: OnChangeFn<SortingState>;
  rowSelection?: RowSelectionState;
  onRowSelectionChange?: OnChangeFn<RowSelectionState>;
  enableRowSelection?: boolean | ((row: Row<TData>) => boolean);
  getRowId?: (originalRow: TData, index: number) => string;
  initialState?: InitialTableState;
  // Mirrors the horizontal scrollbar above the table, for wide tables whose
  // native bar sits below the last row.
  withTopScrollbar?: boolean;
  classNames?: {
    tableCell?: string;
    tableRow?: string;
    header?: string;
    emptyStateLabel?: string;
    tableContainer?: string;
    tableScroller?: string;
  };
}) {
  const table = useReactTable({
    data,
    columns,
    onSortingChange,
    onRowSelectionChange,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: withPagination ? getPaginationRowModel() : undefined,
    getSortedRowModel: getSortedRowModel(),
    state: {
      sorting,
      rowSelection,
    },
    enableRowSelection,
    getRowId,
    manualSorting: true,
    initialState,
  });

  const horizontalScrollbar = useSyncedHorizontalScrollbar();

  // The empty row must span only the headers shown, or a fixed layout splits
  // the free width with a phantom column.
  const hidesCheckboxHeader = !table.getPageCount();
  const shownColumnCount = hidesCheckboxHeader
    ? columns.filter(({ id }) => id !== CHECKBOX_COLUMN_ID).length
    : columns.length;

  return (
    <div className={className}>
      <div
        className={cn(
          'rounded-xl border',
          classNames?.tableContainer,
          'bg-transparent',
        )}
      >
        {withTopScrollbar && horizontalScrollbar.hasOverflow && (
          <div
            ref={horizontalScrollbar.trackRef}
            onPointerDown={horizontalScrollbar.onTrackPointerDown}
            className='relative mx-2 mt-0.5 h-1 rounded-full bg-muted'
          >
            <div
              ref={horizontalScrollbar.thumbRef}
              onPointerDown={horizontalScrollbar.onThumbPointerDown}
              className='absolute inset-y-0 left-0 cursor-grab rounded-full bg-muted-foreground/30 transition-colors hover:bg-muted-foreground/50 active:cursor-grabbing active:bg-muted-foreground/60'
            />
          </div>
        )}
        <Table
          className='relative'
          containerRef={horizontalScrollbar.scrollerRef}
          containerClassName={classNames?.tableScroller}
        >
          <TableHeader>
            {table.getHeaderGroups().map((headerGroup) => (
              <TableRow className={classNames?.tableRow} key={headerGroup.id}>
                {headerGroup.headers.map((header) => {
                  if (
                    header.column.columnDef.id === CHECKBOX_COLUMN_ID &&
                    hidesCheckboxHeader
                  )
                    return null;

                  const headerAlign = getColumnAlign(header.column);
                  const headerWidthClassName = getColumnWidthClassName(
                    header.column,
                  );

                  return (
                    <TableHead
                      className={cn(
                        // Per-column align wins over the table-level `align`.
                        headerAlign
                          ? HEADER_ALIGN_CLASS[headerAlign]
                          : { 'text-center': align === 'center' },
                        isActionsColumn(header.column) && ACTIONS_COLUMN_CLASS,
                        compact && COMPACT_HEADER_CLASS,
                        headerWidthClassName,
                        classNames?.header,
                      )}
                      key={header.id}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(
                            header.column.columnDef.header,
                            header.getContext(),
                          )}
                    </TableHead>
                  );
                })}
              </TableRow>
            ))}
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows?.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow
                  className={cn(
                    tableRowClassName
                      ? typeof tableRowClassName === 'string'
                        ? tableRowClassName
                        : tableRowClassName(row)
                      : '',
                    'table-row',
                    onClickRow && 'cursor-pointer',
                    classNames?.tableRow,
                  )}
                  key={row.id}
                  data-state={row.getIsSelected() && 'selected'}
                  onClick={(ev) => {
                    if (!onClickRow) return;

                    const isInteractiveContent = !!(
                      ev.target as Element
                    ).closest(INTERACTIVE_CELL_CONTENT_SELECTOR);

                    if (isInteractiveContent) return;

                    onClickRow(row);
                  }}
                >
                  {row.getVisibleCells().map((cell) => {
                    const columnAlign = getColumnAlign(cell.column);
                    const columnWidthClassName = getColumnWidthClassName(
                      cell.column,
                    );
                    const content = flexRender(
                      cell.column.columnDef.cell,
                      cell.getContext(),
                    );

                    if (
                      CellWrapper &&
                      !ignoreCellWrapperColumnIds?.includes(cell.column.id)
                    ) {
                      return (
                        <TableCell
                          className={cn(
                            'relative',
                            isActionsColumn(cell.column) &&
                              ACTIONS_COLUMN_CLASS,
                            compact && COMPACT_CELL_CLASS,
                            columnWidthClassName,
                            classNames?.tableCell,
                          )}
                          key={cell.id}
                        >
                          <CellWrapper column={cell.column} row={row.original}>
                            {columnAlign ? (
                              <div
                                className={cn(
                                  'flex items-center',
                                  CELL_ALIGN_CLASS[columnAlign],
                                )}
                              >
                                {content}
                              </div>
                            ) : (
                              content
                            )}
                          </CellWrapper>
                        </TableCell>
                      );
                    }

                    return (
                      <TableCell
                        className={cn(
                          isActionsColumn(cell.column) && ACTIONS_COLUMN_CLASS,
                          compact && COMPACT_CELL_CLASS,
                          columnWidthClassName,
                          classNames?.tableCell,
                        )}
                        key={cell.id}
                      >
                        {columnAlign ? (
                          <div
                            className={cn(
                              'flex items-center',
                              CELL_ALIGN_CLASS[columnAlign],
                            )}
                          >
                            {content}
                          </div>
                        ) : (
                          <div
                            className={cn(
                              {
                                '!pr-4 flex items-center justify-center':
                                  align === 'center' &&
                                  !!(cell.column.columnDef.meta as any)?.align,
                              },
                              'w-fit',
                            )}
                          >
                            {content}
                          </div>
                        )}
                      </TableCell>
                    );
                  })}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={shownColumnCount}
                  className={cn(
                    'h-24 text-center',
                    classNames?.emptyStateLabel,
                  )}
                >
                  {emptyStateMessage}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </div>
      {withPagination && (
        <DataTablePagination entityLabel={entityLabel!} table={table} />
      )}
    </div>
  );
}
